<script setup lang="ts">
	import { QuestionMarkCircleIcon } from '@heroicons/vue/20/solid'
	import moment from 'moment'
	import { computed, ref } from 'vue'
	import { useRoute, useRouter } from 'vue-router'

	import DisclaimerModal from '@/components/DisclaimerModal.vue'
	import ModalComponent from '@/components/ModalComponent.vue'

	import RoomBadge from '@/modules/hotels/components/RoomBadge.vue'
	import useRoom from '@/modules/hotels/composables/useRooms'

	import { useRoomAmenities } from '../composables/useRoomAmenities.js'
	import { Hotel, Room } from '../interfaces/hotelDetailsResponse'
	import { BadgeVariant, getActiveRoomBadges } from '../interfaces/roomBadge.js'
	import { formatProviderDeadline } from '../utils/hotelDates'
	import { roomAmenityDetails } from '../utils/roomAmenities'
	import HotelRatePromotions from './HotelRatePromotions.vue'
	import RateOwnershipConflict from './RateOwnershipConflict.vue'

	moment.localeData('es-mx')

	interface Props {
		room: Room
		hotel: Hotel
		index: number
		modals: {
			[key: string]: { open: (index: number) => void; close: () => void }
		}
		imgUrl: string | undefined
	}

	const props = defineProps<Props>()

	const changeCurrency = ref(false)

	const { query } = useRoute()
	const { push, resolve } = useRouter()

	const reservationNights = Number(
		moment(query.End as string).diff(moment(query.Start as string), 'days')
	)

	const roomType = useRoom(props.room.roomType)

	const nightFareDisclaimer =
		'El costo por noche es un aproximado; puede variar respecto al desglose de costos al momento de realizar la reserva.'

	const disclaimerModal = ref()
	const internationalAlertModal = ref(false)

	const openDisclaimerModal = () => {
		disclaimerModal.value = true
	}

	const restrictedRate = computed(() => !!props.room.errors?.length)
	const cancellationInfo = computed(() => {
		const status = props.room.cancellationStatus ??
			(props.room.deadline ? 'free_until' : props.room.refundable === false ? 'non_refundable' : 'unknown')
		if (status === 'free_until' && props.room.deadline) {
			return `Cancelación sin costo hasta ${formatProviderDeadline(props.room.deadline)}. Después de ese plazo, la tarifa no es reembolsable.`
		}
		if (status === 'cutoff_passed') return 'El plazo de cancelación sin costo terminó. Esta tarifa ya no es reembolsable.'
		if (status === 'non_refundable') return 'No reembolsable'
		return ''
	})

	const breakfastAmenities = computed(() =>
		(props.room.amenities ?? [])
			.map((code) => useRoomAmenities(Number(code))?.[1])
			.filter((name): name is string => !!name && /desayuno.*incluido/i.test(name))
	)

	const breakfastInfo = computed(() => {
		// Hotelbeds manda su régimen oficial («Media pensión», «Solo habitación»): se
		// muestra completo (certificación HBX 3.1) en vez de reducirlo a «desayuno sí/no».
		if (props.room.boardName) {
			const roomOnly = ['RO', 'SC'].includes((props.room.boardCode ?? '').toUpperCase())

			return { label: props.room.boardName, included: !roomOnly }
		}

		const breakfast = props.room.meals?.breakfast
		const plan = props.room.meals?.mealPlanIndicator
		const board = props.room.board?.toUpperCase()
		const explicit = breakfast === '1' || board === 'BB' || board === 'BED AND BREAKFAST'
		const excluded = breakfast === '0' || (breakfast !== '1' && plan === '0')
		const amenity = breakfastAmenities.value[0]

		if (amenity) {
			return {
				label: amenity,
				included: true,
			}
		}
		if (explicit || (!excluded && plan === '1')) {
			return {
				label: 'Desayuno incluido',
				included: true,
			}
		}
		return {
			label: excluded ? 'Desayuno no incluido en esta tarifa' : 'Desayuno no especificado',
			included: false,
		}
	})

	const validAmenities = computed(() => {
		return roomAmenityDetails(props.room.amenities ?? [])
			.filter((amenity) => !/desayuno.*incluido/i.test(amenity.label))
			.sort((a, b) => a.priority - b.priority)
	})

	const displayedAmenities = computed(() => {
		const maxDisplay = 2
		return validAmenities.value.slice(0, maxDisplay)
	})

	const hasMoreAmenities = computed(() => validAmenities.value.length > 2)

	const navigateToRate = () => {
		push(
			resolve({
				name: 'hotel.rate',
				query: {
					...query,
					RoomIndex: props.index,
					RatePlanCode: props.room.ratePlanCode,
					BookingCode: props.room.bookingCode,
					RoomTypeCode: props.room.roomTypeCode,
					// Precio (MXN) que el cliente VIO y aceptó → el backend lo compara contra
					// el re-precio (guard PRICE_MISMATCH). Sin comas para que parse a float.
					AcceptedFare: String(props.room.fareApproximate ?? '').replace(
						/,/g,
						''
					),
					img: props.imgUrl,
				},
			}).href
		)
	}

	const pushToRate = () => {
		if (restrictedRate.value || props.room.blocked) return
		// El modal «pago en destino / tarjeta de garantía» sólo aplica a tarifas de
		// pago-en-destino (Amadeus internacional). Hotelbeds es PREPAGO (paymentModel
		// = 'prepaid'): se cobra por adelantado, sin garantía → no mostrar el modal.
		if (props.room.isInternational && props.room.paymentModel !== 'prepaid') {
			internationalAlertModal.value = true
			return
		}
		navigateToRate()
	}

	const confirmInternationalRate = () => {
		internationalAlertModal.value = false
		navigateToRate()
	}

	const changeTheCurrency = computed(() => {
		if (changeCurrency.value) {
			return `${props.room.fare}  ${props.room.currency}`
		} else {
			return `${props.room.fareApproximate} ${props.room.requestedCurrency}`
		}
	})

	const changeTheCurrencyDaily = computed(() => {
		if (changeCurrency.value) {
			return `${props.room.dailyRate} ${props.room.currency}`
		} else {
			return `${props.room.dailyRateApproximate} ${props.room.requestedCurrency}`
		}
	})

	const onclickChangeCurrency = () => {
		changeCurrency.value = !changeCurrency.value
	}

	const roomBadges = computed<BadgeVariant[]>(() => {
		const data = getActiveRoomBadges(props.room)

		if (data.includes('best-price') && data.includes('deposit')) {
			return data.filter((badge) => badge !== 'best-price')
		}

		return data
	})
</script>

<template>
	<li
		:class="[
			'group flex w-full flex-col rounded-lg sm:rounded-xl bg-white p-3 shadow-md hover:shadow-xl transition-all duration-300 relative border-2 cursor-pointer overflow-hidden',
			'hover:scale-[1.01] hover:-translate-y-1',
			room.agreement && 'border-[#021F58]/20 bg-[#021F58]/5',
			room.hasRestrictions &&
				'border-amber-300 bg-gradient-to-br from-amber-50/30 to-white',
			restrictedRate &&
				'opacity-50 cursor-not-allowed hover:scale-100 hover:translate-y-0',
			!restrictedRate &&
				!room.isLowestDepositRate &&
				!room.hasRestrictions &&
				!room.agreement &&
				'border-gray-200 hover:border-[#021F58]/30',
		]"
	>
		<div v-if="roomBadges.length" class="absolute right-0 top-0 z-10 flex flex-wrap justify-end gap-1">
			<RoomBadge v-for="variant in roomBadges" :key="variant" :variant="variant" />
		</div>
		<!-- Habitación, condiciones de la tarifa y precio -->
		<div :class="['grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_11rem] lg:grid-cols-[minmax(0,1.15fr)_minmax(11rem,0.9fr)_11rem] lg:gap-4', roomBadges.length && 'pt-5']">
			<div class="min-w-0">
			<!-- Columna 1: Nombre de habitación -->
			<div class="flex flex-col gap-1.5">
				<div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm sm:text-base md:text-lg">
					<p class="font-semibold text-gray-900 leading-snug">
						{{ roomType.formatted || room.name }}
					</p>
				</div>

				<div class="flex flex-col items-start gap-1 text-xs sm:text-sm">
					<figure
						v-if="breakfastInfo.included"
						class="flex items-center gap-1 sm:gap-1.5 text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md"
					>
						<span class="material-symbols-outlined text-sm sm:text-base">
							restaurant
						</span>
						<p class="font-medium">{{ breakfastInfo.label }}</p>
					</figure>

					<p
						v-else
						class="text-gray-500 text-xs"
					>
						{{ breakfastInfo.label }}
					</p>
				</div>
				<!-- Amenidades preview -->
				<div
					v-if="validAmenities.length"
					class="flex flex-col gap-1 mt-1"
				>
					<div class="flex flex-wrap items-center gap-1.5">
						<span
							v-for="(amenity, idx) in displayedAmenities"
							:key="idx"
							class="hidden sm:inline-flex max-w-full items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
							:title="amenity.label"
						>
							<span class="material-symbols-outlined text-xs sm:text-sm">{{
								amenity.icon
							}}</span>
							<span class="max-w-[11rem] truncate">{{ amenity.label }}</span>
						</span>

						<button
							v-if="validAmenities.length"
							class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-[#021F58] hover:bg-[#021F58]/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#021F58] min-h-[28px]"
							@click.stop="() => modals.viewMoreDetails.open(index)"
						>
							<span
								class="material-symbols-outlined text-xs sm:text-sm group-hover:translate-x-0.5 transition-transform"
							>
								more_horiz
							</span>
							<span class="sm:hidden">Ver amenidades</span>
							<span v-if="hasMoreAmenities" class="hidden sm:inline">Ver +{{ validAmenities.length - displayedAmenities.length }}</span>
							<span v-else class="hidden sm:inline">Ver todas</span>
						</button>
					</div>
				</div>
			</div>

			</div>
			<!-- Convenio y política: visibles en el centro en escritorio -->
			<div class="flex min-w-0 flex-col items-start gap-2 sm:col-span-2 lg:col-span-1 lg:justify-center">
				<!-- Tarifa Enlaceforte / Company -->
				<div
					v-if="room.agreement || room.companyAgreement"
					class="flex flex-wrap items-center gap-1.5"
				>
					<figure
						v-if="room.agreement"
						class="flex items-center gap-2 w-fit"
					>
						<div
							class="flex items-center gap-1.5 rounded-lg border border-[#021F58]/20 bg-[#021F58]/5 px-2.5 py-1.5 shadow-sm"
						>
							<img
								src="@/assets/imgs/icono_enlace.png"
								class="w-4 h-4 sm:w-5 sm:h-5"
								alt=""
							/>
							<p class="text-xs sm:text-sm font-semibold text-[#021F58]">
								Convenio Enlaceforte
							</p>
						</div>
					</figure>

					<figure
						v-if="room.companyAgreement"
						class="flex min-w-0 max-w-full items-center gap-2 w-fit"
					>
						<div
							class="flex items-center gap-1.5 rounded-lg border border-[#021F58]/20 bg-[#021F58]/5 px-2.5 py-1.5 shadow-sm"
						>
							<img
								v-if="room.companyIsotipo || room.companyLogo"
								:src="room.companyIsotipo || room.companyLogo"
								class="w-4 h-4 sm:w-5 sm:h-5 shrink-0 object-contain"
								alt=""
							/>
							<p class="text-xs sm:text-sm font-semibold text-[#021F58]">
								Convenio {{ room.companyName || 'de empresa' }}
								<span
									v-if="room.companyRateCode"
									class="ml-1 font-mono font-normal text-[#021F58]/70"
									>{{ room.companyRateCode }}</span
								>
							</p>
						</div>
					</figure>
				</div>
				<div
					v-if="cancellationInfo"
					class="flex min-w-0 items-start gap-1.5 text-xs sm:text-sm leading-snug text-gray-700"
				>
					<span class="material-symbols-outlined shrink-0 text-sm" aria-hidden="true">event</span>
					<span>{{ cancellationInfo }}</span>
				</div>

				<!-- Promociones de la tarifa con su código (HBX 2.7); Amadeus no las manda -->
				<HotelRatePromotions compact :promotions="room.promotions" />

				<RateOwnershipConflict
					v-if="room.rateOwnership?.owner === 'conflict'"
				/>

				<!-- Deposit Rate Warning / Mejor Precio -->
				<div
					v-if="room.isLowestDepositRate || room.hasRestrictions"
					class="flex flex-col gap-1.5 sm:gap-2"
				>
					<div
						:class="[
							'inline-flex items-center gap-1 sm:gap-1.5 rounded-lg px-2 py-1 sm:px-2.5 sm:py-1.5 text-xs sm:text-sm font-semibold shadow-sm border w-fit',
							room.isLowestDepositRate ?
								'bg-yellow-50 text-yellow-800 border-yellow-200'
							:	'bg-amber-50 text-amber-800 border-amber-200',
						]"
						v-tooltip="
							room.restrictionWarning ||
							'Esta tarifa requiere depósito anticipado. Restricciones de cancelación y reembolso aplican.'
						"
					>
						<span
							:class="[
								'material-symbols-outlined',
								room.isLowestDepositRate ? 'text-yellow-600' : 'text-amber-600',
							]"
						>
							{{ room.isLowestDepositRate ? 'verified' : 'info' }}
						</span>
						<p>
							{{
								room.isLowestDepositRate ? 'tarifa restrictiva' : (
									'Requiere depósito'
								)
							}}
						</p>
					</div>

					<p :class="['text-xs leading-relaxed', 'text-amber-700']">
						{{
							room.restrictionWarning || 'Restricciones de cancelación aplican.'
						}}
					</p>
				</div>

			</div>

			<!-- Columna 3: Precio y Botón (Derecha) -->
			<div class="flex w-full min-w-0 flex-row flex-wrap items-center justify-between gap-2 sm:col-start-2 sm:row-start-1 sm:flex-col sm:flex-nowrap sm:items-end sm:justify-center sm:gap-1.5 lg:col-start-3">
				<div class="flex min-w-0 flex-col gap-1 text-left sm:text-right">
					<div
						class="text-sm sm:grid sm:text-xl lg:text-2xl"
					>
						<div class="flex gap-1.5 sm:gap-2 items-center">
							<p
								class="w-full whitespace-nowrap text-left font-bold text-blue-ef-100 sm:text-right"
							>
								{{ `$${changeTheCurrencyDaily} ` }}
							</p>
							<span
								class="material-symbols-outlined cursor-pointer text-lg sm:text-xl"
								@click="onclickChangeCurrency"
								v-tooltip="
									changeCurrency ?
										`Ver precio en ${room.requestedCurrency} (Aprox)`
									:	`Ver precio en ${room.currency}`
								"
								v-if="room.requestedCurrency !== room.currency"
							>
								currency_exchange
							</span>
						</div>

						<span
							class="ml-auto flex flex-nowrap items-center gap-0.5 whitespace-nowrap text-[10px] font-light sm:text-xs"
							>por noche · sin impuestos
							<QuestionMarkCircleIcon
								:class="[
									'h-5 w-5 sm:h-6 sm:w-6 cursor-pointer text-gray-400 transition-colors duration-500 hover:text-gray-600',
								]"
								aria-hidden="true"
								@click="openDisclaimerModal()"
							/>
						</span>
					</div>

					<div class="grid">
						<p class="whitespace-nowrap text-base font-semibold sm:text-base lg:text-lg">
							{{ `$${changeTheCurrency} ` }}
						</p>

						<span class="whitespace-nowrap text-[10px] sm:text-xs font-light leading-tight"
							>Total con impuestos</span
						>
					</div>
				</div>

				<button
					class="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-bold text-white shadow-md transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 group sm:w-full"
					@click="pushToRate()"
					:disabled="restrictedRate || room.blocked"
					:class="
						restrictedRate || room.blocked ?
							'bg-gray-400 cursor-not-allowed opacity-60'
						:	'bg-blue-600 hover:shadow-xl hover:scale-105 hover:from-blue-700 hover:to-indigo-700 active:scale-95'
					"
				>
					<span class="whitespace-nowrap sm:hidden">Seleccionar</span>
					<span class="hidden whitespace-nowrap sm:inline">Seleccionar habitación</span>
					<span
						class="material-symbols-outlined text-base sm:text-lg group-hover:translate-x-0.5 transition-transform flex-shrink-0"
					>
						arrow_forward
					</span>
				</button>

				<p
					v-if="room.blocked"
					class="mt-1 w-full text-left text-xs text-red-600 sm:text-right"
				>
					<span class="material-symbols-outlined text-xs align-middle"
						>block</span
					>
					{{
						room.blocked_reason || 'No disponible por políticas de la empresa'
					}}
				</p>
				<p
					v-else-if="restrictedRate && room.errors?.length"
					class="mt-1 w-full text-left text-xs text-red-600 sm:text-right"
				>
					<span class="material-symbols-outlined text-xs align-middle"
						>error</span
					>
					{{ room.errors[0].message }}
				</p>
			</div>
		</div>

		<DisclaimerModal
			:message="nightFareDisclaimer"
			:is-open="disclaimerModal"
			:close="() => (disclaimerModal = false)"
		/>

		<!-- Modal de alerta para tarifas internacionales -->
		<ModalComponent v-model="internationalAlertModal">
			<template #modalBody>
				<div class="p-6">
					<div class="flex items-center gap-3 mb-4">
						<div
							class="flex-shrink-0 w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center"
						>
							<span class="material-symbols-outlined text-indigo-600 text-2xl"
								>public</span
							>
						</div>
						<h3 class="text-lg font-semibold text-gray-900">
							Hotel Internacional - Pago en Destino
						</h3>
					</div>

					<div class="space-y-3 text-sm text-gray-600">
						<p>
							Esta es una tarifa de <strong>hotel internacional</strong>. El
							proceso de pago es diferente:
						</p>
						<ul class="list-disc list-inside space-y-2 ml-2">
							<li>
								Se usara la tarjeta del cliente como <strong>garantía</strong>,
								no se realizará ningún cargo inmediato.
							</li>
							<li>
								El huésped será responsable de realizar el pago directamente en
								el hotel.
							</li>
							<li>
								En caso de no llegar al hotel, se le cargará una penalidad de
								<strong>no-show</strong> a la tarjeta de garantía.
							</li>
						</ul>
						<p class="text-amber-600 font-medium mt-4">
							¿Desea continuar con esta tarifa internacional?
						</p>
					</div>

					<div class="flex justify-end gap-3 mt-6">
						<button
							type="button"
							class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
							@click="internationalAlertModal = false"
						>
							Cancelar
						</button>
						<button
							type="button"
							class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
							@click="confirmInternationalRate()"
						>
							Continuar
						</button>
					</div>
				</div>
			</template>
		</ModalComponent>
	</li>
</template>
